import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GenerateProcess } from "redux-process";

import { faDownload } from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { Avatar } from "@performyard/py-components";

import Button from "../../../../components/Button";
import Icon from "../../../../components/Icon/Icon";
import Modal from "../../../../components/Modal";
import Typography from "../../../../components/Text";

import { getEmployeeName } from "../../../../selectors/users";

import { isImageFile } from "../../helpers";
import { DocumentModalProps } from "../../schema";
import css from "../DocumentsModal.module.scss";
import InfoBanner from "../InfoBanner";

// A dynamic image component that resizes itself to fit the image from parent and maintain aspect ratio
const DynamicImage = ({ src, alt }: any) => {
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

	const handleImageLoad = (event) => {
		const { naturalWidth, naturalHeight } = event.target;
		setDimensions({ width: naturalWidth, height: naturalHeight });
	};

	return (
		<img
			src={src}
			alt={alt}
			onLoad={handleImageLoad}
			style={{
				width: dimensions.width,
				height: dimensions.height,
				maxWidth: "80vw",
				maxHeight: "80vh",
				objectFit: "contain",
			}}
		/>
	);
};

const ViewDocumentModal = ({ show, onClose, document }: DocumentModalProps) => {
	const dispatch = useDispatch();
	const previewUrl = `/document/${document?.id}/${document?.filename}/?show_preview=true`;
	const downloadUrl = `/document/${document?.id}/`;
	const employeeName = useSelector((state) => getEmployeeName(state, document?.uploader, null));
	const isPdf = document?.filename?.endsWith(".pdf");
	const isSignedDocument = Array.isArray(document?.signers);

	// fetch from cache if we already have the uploader's name, else fetch from server
	useEffect(() => {
		if (!employeeName && document?.uploader && show) {
			dispatch(GenerateProcess("GetUserInformation", { userID: document?.uploader }));
		}
	}, [document, show]);

	if (!show) {
		return <></>;
	}

	const getFilePreview = () => {
		if (isPdf) {
			return <iframe title="Preview" src={previewUrl} />;
		}
		if (document?.filename && isImageFile(document?.filename)) {
			return <DynamicImage src={previewUrl} alt={document.filename} />;
		}
		return null;
	};

	const content = (
		<div className={css.viewFileModalContent}>
			<div className={css.metaDataContainer}>
				<div className={css.metaData}>
					<Typography size="paragraph-3" weight="semi-bold">
						File Name
					</Typography>
					<Typography size="paragraph-3" weight="regular" className={css.metaDataValue}>
						{document?.filename}
					</Typography>
				</div>
				<div className={css.metaData}>
					<Typography size="paragraph-3" weight="semi-bold">
						{isSignedDocument ? "Completed on" : "Uploaded on"}
					</Typography>
					<Typography size="paragraph-3" weight="regular" className={css.metaDataValue}>
						{document?.uploadedAt && format(parseISO(document.uploadedAt), "MMMM d, yyyy h:mm a")}
					</Typography>
				</div>
				{employeeName && (
					<div className={css.metaData}>
						<Typography size="paragraph-3" weight="semi-bold">
							Uploaded by
						</Typography>
						<div>
							<Avatar id={document?.uploader} size={20} name={employeeName} />
							<Typography size="paragraph-3" weight="regular" className={css.metaDataValue}>
								{employeeName}
							</Typography>
						</div>
					</div>
				)}
				<div className={css.metaData}>
					<Typography size="paragraph-3" weight="semi-bold">
						Permissions
					</Typography>
					<Typography size="paragraph-3" weight="regular" className={css.metaDataValue}>
						{document && document.vis_to_emp && document.vis_to_man && "Management"}
						{document && document.vis_to_everyone && "Everyone"}
						{document && ((document.vis_to_emp && document.vis_to_man) || document.vis_to_everyone)
							? ", Admins"
							: "Admins Only"}
					</Typography>
				</div>
				{/* Display warning banner if virus scan is still pending on resource */}
				{document && document.pending && (
					<div className={css.metaData}>
						<InfoBanner
							type="warning"
							message="This resource is still being scanned and is potentially unsafe. Download with caution."
							icon={faTriangleExclamation}
						/>
					</div>
				)}
				{/* Display caution banner if virus scan has finished and resource is not clean */}
				{document && !document.pending && !document.clean && (
					<div className={css.metaData}>
						<InfoBanner
							type="error"
							message="This resource failed a virus scan. Download with caution."
							icon={faTriangleExclamation}
						/>
					</div>
				)}
				{document && document.clean && !document.pending && (
					<a href={downloadUrl} download>
						<Button color="green">
							<Icon icon={faDownload} size="small" className={css.downloadIcon} />
							Download
						</Button>
					</a>
				)}
			</div>
			<div className={css.filePreview}>{getFilePreview()}</div>
		</div>
	);

	return <Modal content={content} handleClose={onClose} size={isPdf ? "full" : "auto"} />;
};

export default ViewDocumentModal;
